import * as React from 'react';
import BPromise from 'bluebird';
import { AppUser } from '../App/App';
import { TopNavigation } from '../../components/TopNavigation/TopNavigation';
import { Role } from '../../models/roles';
import { UserProfile } from '../../models/profile';
import { getRoles, getSchool } from '../../helpers/service/admin/user';
import { getSchoolForms, getSchoolHouses } from 'Src/helpers/service/admin/user';
import { Sidebar } from '../../components/Sidebar/Sidebar';
import { Loader } from '../../components/Loader/Loader';
import {
  adminRoutes,
  adminSURoutes,
  coachRoutes,
  managerRoutes,
  parentRoutes,
  studentRoutes,
  teacherRoutes
} from '../../routing/routing';
import { Route, Switch } from 'react-router';
import {
  isUserAdmin,
  isUserAdminSU,
  isUserCoach,
  isUserManager,
  isUserParent,
  isUserStudent,
  isUserTeacher
} from '../../helpers/user/user';
import { Permission } from '../../models/permission';
import { getRequestsCount } from '../../helpers/service/admin/requests';
import { getInboxMessagesCount } from 'Src/helpers/service/admin/messages';
import {
  getAdminSchoolsAllInboxInvites,
  getAdminSchoolsInboxInvitesCount
} from 'Src/helpers/service/adminSU/schoolInvitesTable';
import { getParentNavigationCounts } from 'Src/helpers/service/parent/messagesTable';
import { getStudentNavigationCounts } from 'Src/helpers/service/student/messagesTable';
import { getSchoolWaitingListCount } from 'Src/helpers/service/admin/waitingList';
import { getSchoolUpcomingTournamentsCount } from 'Src/helpers/service/admin/tournaments';
import { WINDOW_BREAKPOINT_FOR_SIDEBAR } from 'Src/consts/responsive';
import { getInboxTournamentConfirmationRequestsCount } from 'Src/helpers/service/admin/messages';
import { getSchoolEventConversationInboxPage } from 'Src/helpers/service/eventCommunications';
import './GenericView.css';
import { PageHelpProvider } from 'Src/components/PageHelp/PageHelpProvider';
import { ParentStudentPageHelp } from 'Src/components/PageHelp/ParentStudentPageHelp';

interface Props {
  user: AppUser;
  onLogoutClick: (event) => void;
  setAppDefaultState: () => void;
  onRoleChange: (
    roleName: string,
    permissionSchoolId: string,
    permissionSchoolName: string,
    permission: Permission
  ) => any;
  onSchoolUpdate: () => void;
  updateProfile: (profile: UserProfile) => void;
  history: any;
  roleOut: () => void;
}

interface State {
  availableRoles: Role[];
  isLoading: boolean;
  needUpdate: boolean;
  isSidebarOpen: boolean;
  windowWidth: number;
  school: any;
  countBadges: {
    requests: string;
    bookings: string;
    inboxMessages: string;
    invites: string;
    tournamentInvites: string;
    waitingList: string;
    upcomingTournaments: string;
    availabilityReports: string;
    absenceRequests: string;
    tournamentConfirmationRequests: string;
    eventCommunications: string;
  };
}

export class GenericView extends React.Component<Props, State> {
  private badgeCountRequestId = 0;
  private navigationCountsRefreshTimer?: number;

  constructor(props: Props) {
    super(props);

    this.state = {
      availableRoles: [],
      isLoading: true,
      needUpdate: false,
      isSidebarOpen: true,
      windowWidth: 0,
      school: null,
      countBadges: {
        requests: '',
        bookings: '',
        inboxMessages: '',
        invites: '',
        tournamentInvites: '',
        waitingList: '',
        upcomingTournaments: '',
        availabilityReports: '',
        absenceRequests: '',
        tournamentConfirmationRequests: ''
        ,eventCommunications: ''
      }
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateWindowDimensions);
    document.addEventListener('visibilitychange', this.refreshTimeSensitiveBadges);
    this.navigationCountsRefreshTimer = window.setInterval(this.refreshTimeSensitiveBadges, 30000);

    this.setState({
      windowWidth: window.innerWidth,
      isSidebarOpen: window.innerWidth > WINDOW_BREAKPOINT_FOR_SIDEBAR
    });

    this.getRoles();
    this.setCountsForBadges();

    // Fetch school data if necessary
    this.fetchSchoolDataIfNeeded(this.props.history.location.pathname);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
    document.removeEventListener('visibilitychange', this.refreshTimeSensitiveBadges);
    if (typeof this.navigationCountsRefreshTimer !== 'undefined') {
      window.clearInterval(this.navigationCountsRefreshTimer);
    }
  }

  componentDidUpdate(prevProps) {
    const { needUpdate } = this.state;
    let badgesRefreshRequested = false;

    if (needUpdate) {
      this.setCountsForBadges();
      this.unsetNeedUpdate();
      badgesRefreshRequested = true;
    }

    // Fetch school data if the route changes to import-students
    if (this.props.history.location.pathname !== prevProps.history.location.pathname) {
      this.fetchSchoolDataIfNeeded(this.props.history.location.pathname);
      if (!badgesRefreshRequested) this.refreshTimeSensitiveBadges();
    }
  }

  refreshTimeSensitiveBadges = () => {
    const { user } = this.props;
    if (document.hidden || (!isUserParent(user) && !isUserStudent(user))) return;
    this.setCountsForBadges();
  };

  updateWindowDimensions = () => {
    this.setState({ windowWidth: window.innerWidth });
  };

  getRoles() {
    const user = this.props.user;
    this.setState({ isLoading: true });

    getRoles(user).then((roles: Role[]) => {
      this.setState({
        availableRoles: roles,
        isLoading: false
      });
    });
  }

  fetchSchoolDataIfNeeded(pathname: string) {
    if (pathname === '/import-students' && !this.state.school) {
      this.fetchSchoolData();
    }
  }

  fetchSchoolData() {
    const { user } = this.props;
    this.setState({ isLoading: true });

    getSchool(user, user.activeSchoolId)
      .then(school => {
        // Fetch forms and houses
        return Promise.all([school, getSchoolForms(user), getSchoolHouses(user)]);
      })
      .then(([school, forms, houses]) => {
        // Attach forms and houses to the school object
        const fullSchoolData = { ...school, forms, houses };
        this.setState({ school: fullSchoolData, isLoading: false });
      })
      .catch(err => {
        console.error('Failed to fetch school data:', err);
        this.setState({ school: null, isLoading: false });
      });
  }

  setCountsForBadges() {
    const requestId = ++this.badgeCountRequestId;
    const { user } = this.props;
    const isAdmin = isUserAdmin(user);
    const isManager = isUserManager(user);
    const isCoach = isUserCoach(user);
    const isTeacher = isUserTeacher(user);
    const isParent = isUserParent(user);
    const isStudent = isUserStudent(user);

    if (isAdmin || isManager) {
      const emptyCount = { count: 0 };
      const emptyNumberCount = 0;
      const safeCount = <T extends unknown>(promise: Promise<T>, fallback: T) => promise.catch(() => fallback);

      const requestsCount = safeCount(getRequestsCount(user), emptyCount);
      const inboxMessagesCount = safeCount(getInboxMessagesCount(user), emptyCount);
      const inboxInvitesCount = safeCount(getAdminSchoolsInboxInvitesCount(user), emptyCount);
      const inboxTournamentInvitesCount = isAdmin
        ? safeCount(
            getAdminSchoolsAllInboxInvites(user, { kind: 'TournamentSchoolInvite' }).then(invites => ({
              count: invites.filter(invite => {
                const startTime = invite && invite.tournament && invite.tournament.startTime;
                if (!startTime) return false;
                const startDate = new Date(startTime);
                return !Number.isNaN(startDate.getTime()) && startDate.getTime() > Date.now();
              }).length
            })),
            emptyCount
          )
        : Promise.resolve(emptyCount);
      const waitingListCount = safeCount(getSchoolWaitingListCount(user), emptyCount);
      const upcomingTournaments = isAdmin
        ? safeCount(getSchoolUpcomingTournamentsCount(user), emptyCount)
        : Promise.resolve(emptyCount);
      const tournamentConfirmationRequests = isAdmin
        ? safeCount(getInboxTournamentConfirmationRequestsCount(user, {}), emptyNumberCount)
        : Promise.resolve(emptyNumberCount);
      const eventCommunications = safeCount(
        getSchoolEventConversationInboxPage(user, {where: {scope: 'PERSONAL'}, limit: 1})
          .then(page => Number(page.facets && page.facets.unread || 0)),
        emptyNumberCount
      );
      const promises: Promise<any>[] = [
        requestsCount,
        inboxMessagesCount,
        inboxInvitesCount,
        inboxTournamentInvitesCount,
        waitingListCount,
        upcomingTournaments,
        tournamentConfirmationRequests,
        eventCommunications
      ];

      return Promise.all(promises).then(
        ([
          requestsCountObj,
          inboxMessagesCountObj,
          inboxInvitesCountObj,
          inboxTournamentInvitesCountObj,
          waitingListCount,
          upcomingTournaments,
          tournamentConfirmationRequests,
          eventCommunications
        ]) => {
          if (requestId !== this.badgeCountRequestId) return;
          const countBadgesUpdated = {
            requests: `${requestsCountObj.count}`,
            bookings: '',
            inboxMessages: `${inboxMessagesCountObj.count}`,
            invites: `${inboxInvitesCountObj.count}`,
            tournamentInvites: `${inboxTournamentInvitesCountObj.count}`,
            waitingList: `${waitingListCount.count}`,
            upcomingTournaments: `${upcomingTournaments.count}`,
            availabilityReports: '',
            absenceRequests: '',
            tournamentConfirmationRequests: `${tournamentConfirmationRequests}`,
            eventCommunications: `${eventCommunications}`
          };

          this.setState({
            countBadges: countBadgesUpdated
          });
        }
      );
    }

    if (isCoach || isTeacher) {
      const inboxMessagesCount = getInboxMessagesCount(user);
      const inboxInvitesCount = getAdminSchoolsInboxInvitesCount(user);

      const eventCommunications = getSchoolEventConversationInboxPage(user, {where: {scope: 'PERSONAL'}, limit: 1})
        .then(page => Number(page.facets && page.facets.unread || 0)).catch(() => 0);
      const promises = [inboxMessagesCount, inboxInvitesCount, eventCommunications];

      return Promise.all(promises).then((results: any[]) => {
        const [inboxMessagesCountObj, inboxInvitesCountObj, eventCommunications] = results;
        if (requestId !== this.badgeCountRequestId) return;
        const countBadgesUpdated = {
          requests: '',
          bookings: '',
          inboxMessages: `${inboxMessagesCountObj.count}`,
          invites: `${inboxInvitesCountObj.count}`,
          tournamentInvites: '',
          availabilityReports: '',
          waitingList: '',
          upcomingTournaments: '',
          absenceRequests: '',
          tournamentConfirmationRequests: '',
          eventCommunications: `${eventCommunications}`
        };

        this.setState({
          countBadges: countBadgesUpdated
        });
      });
    }

    if (isParent) {
      return getParentNavigationCounts(user)
        .then(counts => {
          if (requestId !== this.badgeCountRequestId) return;
          const countBadgesUpdated = {
            requests: `${counts.consentRequests}`,
            bookings: `${counts.clubInvitations}`,
            inboxMessages: '',
            availabilityReports: `${counts.availabilityReports}`,
            absenceRequests: `${counts.absenceRequests}`,
            invites: '',
            tournamentInvites: '',
            waitingList: '',
            upcomingTournaments: '',
            tournamentConfirmationRequests: '',
            eventCommunications: ''
          };

          this.setState({
            countBadges: countBadgesUpdated
          });
        })
        .catch(() => undefined);
    }

    if (isStudent) {
      return getStudentNavigationCounts(user)
        .then(counts => {
          if (requestId !== this.badgeCountRequestId) return;
          const countBadgesUpdated = {
            requests: '',
            bookings: `${counts.clubInvitations}`,
            inboxMessages: '',
            availabilityReports: `${counts.availabilityReports}`,
            invites: '',
            tournamentInvites: '',
            waitingList: '',
            upcomingTournaments: '',
            absenceRequests: '',
            tournamentConfirmationRequests: '',
            eventCommunications: ''
          };

          this.setState({
            countBadges: countBadgesUpdated
          });
        })
        .catch(() => undefined);
    }
  }

  setNeedUpdate = () => {
    this.setState({
      needUpdate: true
    });
  };

  unsetNeedUpdate = () => {
    this.setState({
      needUpdate: false
    });
  };

  onSidebarSwitch = () => {
    this.setState(state => ({
      isSidebarOpen: !state.isSidebarOpen
    }));
  };

  render() {
    const {
      isLoading,
      availableRoles,
      needUpdate,
      isSidebarOpen,
      countBadges,
      windowWidth,
      school
    } = this.state;
    const { user } = this.props;

    const sidebarClasses =
      windowWidth > WINDOW_BREAKPOINT_FOR_SIDEBAR
        ? 'bGenericSidebarColumn'
        : 'bGenericSidebarColumn bGenericSidebarColumn--mobile';

    const contentClasses =
      isSidebarOpen && windowWidth > WINDOW_BREAKPOINT_FOR_SIDEBAR
        ? 'bGenericContentColumn'
        : 'bGenericContentColumn bGenericContentColumn--full';

    if (isLoading) {
      return <Loader />;
    }

    let routes;

    switch (true) {
      case isUserAdminSU(user):
        routes = adminSURoutes;
        break;
      case isUserParent(user):
        routes = parentRoutes;
        break;
      case isUserStudent(user):
        routes = studentRoutes;
        break;
      case isUserAdmin(user):
        routes = adminRoutes;
        break;
      case isUserManager(user):
        routes = managerRoutes;
        break;
      case isUserTeacher(user):
        routes = teacherRoutes;
        break;
      case isUserCoach(user):
        routes = coachRoutes;
        break;
      default:
        routes = studentRoutes;
    }

    return (
      <PageHelpProvider pathname={this.props.history.location.pathname}>
        {(isUserParent(user) || isUserStudent(user)) && (
          <ParentStudentPageHelp
            audience={isUserParent(user) ? 'parent' : 'student'}
            pathname={this.props.history.location.pathname}
            user={user}
            availableRoles={availableRoles}
            history={this.props.history}
          />
        )}
        <div className={isUserAdminSU(user) ? 'bGenericView bGenericView--union' : 'bGenericView'}>
          <TopNavigation
            {...this.props}
            availableRoles={availableRoles}
            onSidebarSwitch={this.onSidebarSwitch}
          />
          <div className="container-fluid bGenericViewShell">
            <div className="row bGenericViewRow">
              {isSidebarOpen && (
                <div className={sidebarClasses}>
                  <Sidebar
                    {...this.props}
                    availableRoles={availableRoles}
                    unsetNeedUpdate={this.unsetNeedUpdate}
                    needUpdate={needUpdate}
                    countBadges={countBadges}
                    onSidebarCloseClick={this.onSidebarSwitch}
                    windowWidth={windowWidth}
                  />
                </div>
              )}

              <div className={contentClasses}>
                <Switch>
                  {routes.map((route, i) => (
                    <Route
                      exact={route.exact}
                      key={`route_${i}`}
                      path={route.path}
                      render={routeProps => (
                        <route.component
                          {...this.props}
                          {...this.state}
                          setNeedUpdate={this.setNeedUpdate}
                          school={school} // Pass the school data to the routed component
                        />
                      )}
                    />
                  ))}
                </Switch>
              </div>
            </div>
          </div>
        </div>
      </PageHelpProvider>
    );
  }
}
